import { createAppRequest } from '@/services/request';
import type {
  WorkOrder,
  WorkOrderFieldDefinition,
  WorkOrderNodeDefinition,
  WorkOrderAttachment,
  WorkOrderFormExtensions,
  WorkOrderTemplateVersion,
  WorkOrderTemplate,
  WorkbenchOverview,
} from './types/work-order';

const request = createAppRequest();
const base = '/bff/work-orders';

export const workOrderApi = {
  listTemplates: () => request<WorkOrderTemplate[]>(`${base}/templates`),
  getTemplate: (uuid: string) => request<WorkOrderTemplate>(`${base}/templates/${uuid}`),
  saveTemplate: (data: {
    name: string;
    code: string;
    description?: string;
    isEnabled?: boolean;
    launchScope?: Record<string, unknown>;
    statCategory?: string;
    slaConfig?: { warningMinutes?: number };
    fields: WorkOrderFieldDefinition[];
    nodes: WorkOrderNodeDefinition[];
    formSchema?: WorkOrderTemplateVersion['formSchema'];
    uiSchema?: WorkOrderTemplateVersion['uiSchema'];
    formExtensions?: WorkOrderFormExtensions;
  }, uuid?: string) => request<WorkOrderTemplate>(uuid ? `${base}/templates/${uuid}` : `${base}/templates`, { method: uuid ? 'PUT' : 'POST', data }),
  copyTemplate: (uuid: string) => request<WorkOrderTemplate>(`${base}/templates/${uuid}/copy`, { method: 'POST', data: {} }),
  publishTemplate: (uuid: string) => request<WorkOrderTemplate>(`${base}/templates/${uuid}/publish`, { method: 'POST' }),
  create: (data: { templateUuid: string; title: string; priority?: string; source?: string; formData: Record<string, unknown> }) => request<WorkOrder>(base, { method: 'POST', data }),
  list: (params?: Record<string, unknown>) => request<{ data: WorkOrder[]; total: number; page: number; pageSize: number }>(base, { params }),
  detail: (uuid: string) => request<WorkOrder>(`${base}/${uuid}`),
  detailMine: (uuid: string) => request<WorkOrder>(`${base}/mine/${uuid}`),
  action: (uuid: string, data: Record<string, unknown>) => request<WorkOrder>(`${base}/${uuid}/actions`, { method: 'POST', data }),
  workbench: () => request<{ pending: number; processing: number; closed: number; overdue: number; myTodo: number }>(`${base}/workbench/summary`),
  workbenchOverview: () => request<WorkbenchOverview>(`${base}/workbench/overview`),
  statistics: (days = 30) => request<import('./types/work-order').WorkOrderStatistics>(`${base}/statistics/overview`, { params: { days } }),
  retryNotification: (uuid: string) => request(`${base}/notifications/${uuid}/retry`, { method: 'POST' }),
  uploadAttachment: (file: File, fieldCode = 'attachments', workOrderUuid?: string) => {
    const data = new FormData();
    data.append('file', file);
    data.append('fieldCode', fieldCode);
    if (workOrderUuid) data.append('workOrderUuid', workOrderUuid);
    return request<WorkOrderAttachment>(`${base}/attachments`, { method: 'POST', data });
  },
  previewAttachment: (uuid: string) => request<WorkOrderAttachment>(`${base}/attachments/${uuid}/preview`),
  downloadAttachment: (uuid: string) => request<WorkOrderAttachment>(`${base}/attachments/${uuid}/download`),
  deleteAttachment: (uuid: string) => request(`${base}/attachments/${uuid}`, { method: 'DELETE' }),
};
